import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { analyticsService } from "@/services/AnalyticsService";
import { logger } from "@/lib/logger";
import { isChunkLoadError, triggerChunkReload } from "@/lib/errorHandlers";
import { ErrorCard } from "./ErrorCard";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  variant?: 'full-page' | 'inline'; // New: support different display modes
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Unified Premium Error Boundary.
 * Handles both critical app-level crashes and feature-level interruptions.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      const reloaded = triggerChunkReload('error_boundary_chunk');
      if (reloaded) {
        logger.warn('Dynamic chunk import failure caught by ErrorBoundary; page reload initiated.', {
          error: error.message,
        });
        return;
      }
    }

    const errorMetadata = {
      componentStack: errorInfo.componentStack,
      component: 'ErrorBoundary',
      type: 'react_crash',
      variant: this.props.variant || 'inline'
    };

    // 1. Log to high-integrity logger
    logger.error('Uncaught UI Exception', error, errorMetadata);

    // 2. Track to analytics for product quality monitoring
    analyticsService.error(error, errorMetadata);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  private renderFullPage() {
    const isChunkError = isChunkLoadError(this.state.error);

    return (
      <div className="min-h-screen flex items-center justify-center p-6 overflow-hidden relative bg-transparent selection:bg-purple-500/30">
        {/* Stable Ambient Fade - Simple Overlay */}
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
        
        {/* Subtle glow behind card only */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(60,30,120,0.15)_0%,_transparent_70%)] pointer-events-none" />

        <div className="relative z-10 bg-white/[0.03] backdrop-blur-3xl border border-white/10 rounded-[32px] p-8 max-w-sm w-full text-center shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-1000 ring-1 ring-white/5">
          {/* Inner Vignette for Depth */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_40%,_rgba(0,0,0,0.4)_100%)] pointer-events-none" />
          
          <div className="relative z-10 flex justify-center mb-6">
            <div className="relative">
              <div className="w-16 h-16 bg-red-500/5 rounded-full flex items-center justify-center border border-red-500/20 animate-pulse">
                <AlertTriangle className="w-8 h-8 text-red-400/80" />
              </div>
              <div className="absolute -inset-4 bg-red-500/5 blur-xl rounded-full -z-10" />
            </div>
          </div>

          <h2 className="text-2xl font-black text-white mb-2 tracking-tighter font-hebrew">
            {isChunkError ? "עדכון גרסה במערכת" : "משהו השתבש"}
          </h2>
          <p className="text-mystic-400 mb-8 text-sm leading-relaxed font-hebrew font-medium">
            {isChunkError
              ? "גרסת האפליקציה עודכנה ברקע. מרענן את העמוד..."
              : "אירעה שגיאה לא צפויה."}
          </p>

          <div className="flex flex-col gap-4">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-[18px] transition-all duration-500 shadow-2xl shadow-purple-500/10 font-bold text-lg active:scale-[0.98] group"
            >
              <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-700" />
              <span className="font-hebrew">רענון</span>
            </button>
          </div>

          {import.meta.env.DEV && this.state.error && (
            <div className="mt-8 text-left bg-black/40 rounded-2xl border border-white/5 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-1000">
              <div className="px-3 py-1.5 border-b border-white/5 bg-white/5 flex items-center justify-between">
                <span className="text-[9px] font-black text-mystic-500 uppercase tracking-widest">Debug Output</span>
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500/40" />
                  <div className="w-1.5 h-1.5 rounded-full bg-gold-500/40" />
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500/40" />
                </div>
              </div>
              <div data-no-swipe="true" className="p-4 max-h-32 overflow-auto scrollbar-none">
                <code className="text-[10px] text-red-300/80 font-mono whitespace-pre-wrap leading-relaxed">
                  {this.state.error.toString()}
                </code>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  private renderInline() {
    const isChunkError = isChunkLoadError(this.state.error);
    return (
      <ErrorCard
        message={isChunkError
          ? "גרסת האפליקציה עודכנה ברקע. מרענן את העמוד..."
          : "לא הצלחנו לטעון רכיב זה. ניתן לנסות לטעון אותו מחדש מבלי לרענן את כל העמוד."}
        onRetry={isChunkError ? () => window.location.reload() : this.handleReset}
        title={isChunkError ? "עדכון גרסה" : "שגיאה בתצוגה"}
      />
    );
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return this.props.variant === 'full-page' ? this.renderFullPage() : this.renderInline();
    }

    return this.props.children;
  }
}
